import { Head, Link, router } from '@inertiajs/react'
import { useMemo } from 'react'
import AppLayout from '@/components/AppLayout.tsx'
import { plan_index_path, plan_top_down_path, plan_top_down_revision_request_path } from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'
import { Button } from '@/components/ui/Button'
import PlanNavRow from '../components/PlanNavRow'
import PlanHeader from '../components/PlanHeader'
import ConnectedTaskLine, { type CycleTask } from '../components/ConnectedTaskLine'
import PeriodPicker from './components/PeriodPicker'
import { type PlanNodeFromServer } from './components/VersionViewOverallSection'
import PlanBreakdownSections, { type ChannelSectionData, type QuarterlySectionData } from './components/PlanBreakdownSections'
import { type PlanSwitcherData } from './components/PlanSwitcher'
import { rowKey, type RowAnchor } from './components/rowAnchor'
import { type DraftRequest } from './components/revisionRequest'
import { revisionLabels } from './components/periodLabel'

interface Props {
  slug: string
  begin_date: string
  end_date: string
  top_down_plan: {
    id: string
    name: string
    slug: string
    revenue_target: number
    currency: string | null
    org_node: { id: string; name: string; slug: string } | null
  }
  period: { start: string; end: string }
  // The plan period a revision's numbers cover (target months only); the
  // months before it are actual sales. Present for every version, but only
  // meaningful (narrower than begin_date–end_date) for a revision.
  plan_period: { start: string; end: string }
  // Number of months at the start of the plan already covered by actual
  // sales. Null for a plain version.
  actuals_months: number | null
  // Null for a draft-only plan that has no version rows yet.
  version: {
    id: string
    version_number: number | null
    name: string | null
    note: string | null
    published_at: string | null
    is_current: boolean
    actuals_months: number | null
  } | null
  plan_nodes: PlanNodeFromServer[]
  channel_section: ChannelSectionData
  quarterly_section: QuarterlySectionData
  // Every open planning task on this plan (cycle, quarterly, a manager's
  // request), oldest first; empty once they are done.
  cycle_tasks: CycleTask[]
  // A draft exists on a published plan; the recap keeps showing the
  // published numbers.
  revision_in_progress: boolean
  plan_switcher: PlanSwitcherData
  draft_request: DraftRequest | null
  can: { manage_top_down: boolean; request_revision: boolean }
}

// Read-only landing page for the session-selected plan: recaps the latest
// published version, or the draft for a plan that has never been published.
export default function Show({
  slug,
  begin_date,
  end_date,
  top_down_plan,
  period,
  plan_period,
  actuals_months,
  version,
  plan_nodes,
  channel_section,
  quarterly_section,
  cycle_tasks,
  revision_in_progress,
  plan_switcher,
  draft_request,
  can,
}: Props) {
  const market = useMarket()
  const requestPath = plan_top_down_revision_request_path(market.slug, slug)
  const goComment = (anchor: RowAnchor) => router.visit(`${requestPath}?row=${encodeURIComponent(rowKey(anchor))}`)
  // Month labels for a revision's actual-vs-plan split; null for a plain
  // version, so every column that shows one carries its months.
  const periodLabels = useMemo(
    () => revisionLabels(plan_period, actuals_months, begin_date, period),
    [plan_period, actuals_months, begin_date, period]
  )
  // Full year = actual sales (top-level nodes only, to avoid double-counting
  // descendants) + this version's plan target.
  const fullYearTarget = useMemo(
    () => plan_nodes.reduce((sum, n) => sum + Number(n.actualRevenue ?? 0), 0) + top_down_plan.revenue_target,
    [plan_nodes, top_down_plan.revenue_target]
  )
  return (
    <AppLayout
      header={<PlanHeader
        name={top_down_plan.name}
        orgNodeName={top_down_plan.org_node?.name ?? null}
        revenueTarget={top_down_plan.revenue_target}
        currency={top_down_plan.currency}
        beginDate={begin_date}
        endDate={end_date}
        planPeriod={plan_period}
        actualsMonths={actuals_months}
        fullYearTarget={periodLabels ? fullYearTarget : null}
      />}
    >
      <Head title={top_down_plan.name} />
      <PlanNavRow planSwitcher={plan_switcher} className="mb-4" />
      <div className="mb-3 grid grid-cols-[1fr_auto_1fr] items-center gap-4">
        <div
          style={{ background: 'var(--background)', borderColor: 'var(--border)' }}
          className="justify-self-start border rounded-lg px-4 py-2.5"
        >
          <PeriodPicker
            planId={top_down_plan.id}
            beginDate={begin_date}
            endDate={end_date}
            start={period.start}
            end={period.end}
            path={plan_index_path(market.slug)}
            only={['period', 'plan_nodes', 'channel_section', 'quarterly_section']}
          />
        </div>
        <div
          style={{ background: 'var(--background)', borderColor: 'var(--border)' }}
          className="border rounded-lg px-3 py-1.5 text-[13px] text-foreground text-center"
        >
          {version?.published_at
            ? 'You are viewing the latest published version of this plan.'
            : 'You are viewing a draft of this plan.'}
          {revision_in_progress && ' A revision is in progress.'}
          {version?.actuals_months
            ? ` This version has ${version.actuals_months} months of actual sales and plans the rest.`
            : null}
          {cycle_tasks.map((task) => <ConnectedTaskLine key={task.id} task={task} />)}
          {draft_request && (
            <div className="mt-1">
              You have {draft_request.comment_count} unsent {draft_request.comment_count === 1 ? 'comment' : 'comments'} on this plan,
              kept until {new Date(draft_request.kept_until + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              {' · '}
              <Link href={requestPath} className="text-link hover:underline">Continue</Link>
            </div>
          )}
        </div>
        {can.manage_top_down && (
          <Button
            size="sm"
            className="justify-self-end"
            onClick={() => router.visit(plan_top_down_path(market.slug, slug))}
          >
            Work on this plan
          </Button>
        )}
      </div>
      <PlanBreakdownSections
        planNodes={plan_nodes}
        channelSection={channel_section}
        quarterlySection={quarterly_section}
        currency={top_down_plan.currency}
        periodLabels={periodLabels}
        onRowAction={can.request_revision ? goComment : undefined}
      />
    </AppLayout>
  )
}
